import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

/**
 * The market (area) the admin is working in. `undefined` means "all markets I can see";
 * the gateway still narrows every search to the caller's own markets.
 */
interface MarketState {
  areaId: number | undefined;
  setAreaId(id: number | undefined): void;
  /** areaIds for search criteria: the selected market, or undefined for server-side scoping. */
  areaIds: number[] | undefined;
  /** Register a confirmation for transient work before the header changes market. */
  guardMarketChange(guard: () => Promise<boolean>): () => void;
}

const STORAGE_KEY = 'selectedAreaId';
const MarketContext = createContext<MarketState | null>(null);

function initial(): number | undefined {
  try {
    const n = Number(localStorage.getItem(STORAGE_KEY));
    return Number.isFinite(n) && n > 0 ? n : undefined;
  } catch {
    return undefined;
  }
}

export function MarketProvider({ children }: { children: ReactNode }) {
  const guards = useRef(new Set<() => Promise<boolean>>());
  const request = useRef(0);
  const [areaId, setArea] = useState<number | undefined>(initial);
  const value = useMemo<MarketState>(
    () => ({
      areaId,
      areaIds: areaId ? [areaId] : undefined,
      setAreaId(id) {
        if (id === areaId) return;
        const generation = ++request.current;
        const apply = () => {
          if (generation !== request.current) return;
          setArea(id);
          try {
            if (id) localStorage.setItem(STORAGE_KEY, String(id));
            else localStorage.removeItem(STORAGE_KEY);
          } catch {
            /* ignore */
          }
        };
        if (guards.current.size === 0) return apply();
        void (async () => {
          for (const guard of guards.current) {
            if (!(await guard()) || generation !== request.current) return;
          }
          apply();
        })();
      },
      guardMarketChange(guard) {
        guards.current.add(guard);
        return () => { guards.current.delete(guard); };
      },
    }),
    [areaId],
  );
  return <MarketContext.Provider value={value}>{children}</MarketContext.Provider>;
}

export function useMarket(): MarketState {
  const ctx = useContext(MarketContext);
  if (!ctx) throw new Error('useMarket outside MarketProvider');
  return ctx;
}

/** Protect transient map edits when changing market without navigating to another URL. */
export function useMarketSwitchGuard(guard: () => Promise<boolean>): void {
  const { guardMarketChange } = useMarket();
  const latest = useRef(guard);
  latest.current = guard;
  useEffect(() => guardMarketChange(() => latest.current()), [guardMarketChange]);
}
